import { useState } from "react";
import { Home, Sparkles, ArrowRight } from "lucide-react";
import { useProjects } from "@contexts/ProjectContext";

/**
 * First-run project creation wizard for homeowners. Opens after the
 * PersonaPicker sets userType: "homeowner" AND the user has no
 * existing project. Collects name + address (+ optional sqft),
 * calls createV2Project which writes projects/{pid} + an owner
 * member doc + seeded blank data in one batched write.
 *
 * Skippable: "I'll fill it in later" creates a project named "My
 * Renovation" with blank everything. User can rename it from Settings.
 */
export default function OnboardingWizard() {
  const { createV2Project } = useProjects();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [sqFt, setSqFt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (payload) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await createV2Project(payload);
    } catch (err) {
      console.error("[OnboardingWizard] create failed", err);
      setError(err?.message ?? "Something went wrong — please try again.");
      setSubmitting(false);
    }
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    submit({
      name: trimmed || "My Renovation",
      address: address.trim(),
      sqFt: sqFt ? Number(sqFt) : null,
    });
  };

  const handleSkip = () => {
    submit({ name: "My Renovation" });
  };

  const inputStyle = {
    background: "var(--bg-input)",
    border: "1px solid var(--border-input)",
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        zIndex: 2147483644,
        background: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        className="rounded-xl w-full max-w-lg p-6"
        style={{
          background: "var(--bg-root)",
          border: "1px solid var(--border-card)",
          boxShadow:
            "0 24px 60px rgba(0,0,0,0.55), 0 6px 20px rgba(0,0,0,0.35)",
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4" style={{ color: "var(--accent-text)" }} />
          <p
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color: "var(--text-secondary)" }}
          >
            Let's set up your project
          </p>
        </div>
        <h2
          className="text-xl font-bold mb-1 flex items-center gap-2"
          style={{ color: "var(--text-heading)" }}
        >
          <Home className="w-5 h-5" />
          Tell us about the renovation
        </h2>
        <p
          className="text-sm leading-relaxed mb-5"
          style={{ color: "var(--text-body)" }}
        >
          You can change everything later. The more you fill in now, the more
          useful the AI advisor will be from day one.
        </p>

        <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label
              className="text-[10px] uppercase tracking-wider font-semibold block mb-1"
              style={{ color: "var(--text-secondary)" }}
            >
              Project name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kitchen + Great Room Addition"
              className="w-full text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{ ...inputStyle, color: "var(--text-heading)" }}
              autoFocus
            />
          </div>

          <div>
            <label
              className="text-[10px] uppercase tracking-wider font-semibold block mb-1"
              style={{ color: "var(--text-secondary)" }}
            >
              Property address
            </label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g. 1234 Elm St, Somewhere, CA 95070"
              className="w-full text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{ ...inputStyle, color: "var(--text-heading)" }}
            />
          </div>

          <div>
            <label
              className="text-[10px] uppercase tracking-wider font-semibold block mb-1"
              style={{ color: "var(--text-secondary)" }}
            >
              Approx. scope (sq ft) — optional
            </label>
            <input
              type="number"
              min="0"
              step="10"
              value={sqFt}
              onChange={(e) => setSqFt(e.target.value)}
              placeholder="e.g. 330"
              className="w-full text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{ ...inputStyle, color: "var(--text-heading)" }}
            />
          </div>

          {error && (
            <p className="text-[11px]" style={{ color: "#ef4444" }}>
              {error}
            </p>
          )}

          <div className="flex items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              className="text-xs font-medium disabled:opacity-60"
              style={{ color: "var(--text-secondary)" }}
            >
              I'll fill it in later
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              {submitting ? "Creating…" : "Create project"}
              {!submitting && <ArrowRight className="w-3.5 h-3.5" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
